import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertCircle } from 'react-feather';
import ToolCard from '../components/Tools/ToolCard';
import Button from '../components/UI/Button';
import { toolsConfig } from '../config/toolsConfig';
import useTranslation from '../hooks/useTranslation';

const CategoryPage = () => {
  const { category } = useParams();
  const categoryData = toolsConfig[category];
  const { t } = useTranslation();

  if (!categoryData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen flex items-center justify-center"
      >
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('category.notFound.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('category.notFound.message', `The category "${category}" doesn't exist.`, { category })}
          </p>
          <Link to="/">
            <Button>
              <ArrowLeft className="h-4 w-4" />
              {t('category.backHome')}
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  }

  const IconComponent = categoryData.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen py-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('category.backHome')}
          </Link>
        </motion.nav>

        {/* Category Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className={`p-4 ${categoryData.color} rounded-xl`}>
              <IconComponent className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                {t('category.header.tools', `${categoryData.name} Tools`, { name: t(`nav.${category}`, categoryData.name) })}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mt-2">
                {t(`category.description.${category}`, categoryData.description)}
              </p>
            </div>
          </div>

          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <p className="text-primary-800 dark:text-primary-200">
              <strong>{t('home.categories.toolsAvailable', `${Object.keys(categoryData.tools).length} tools available`, { count: Object.keys(categoryData.tools).length })}</strong> {t('category.header.count')}
            </p>
          </div>
        </motion.div>

        {/* Tools Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {Object.entries(categoryData.tools).map(([toolKey, tool], index) => (
            <motion.div
              key={toolKey}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
            >
              <ToolCard
                tool={tool}
                toolKey={toolKey}
                category={category}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Coming Soon Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-gray-100 dark:bg-dark-800 rounded-xl p-8 border-2 border-dashed border-gray-300 dark:border-dark-600">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('category.comingSoon.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('category.comingSoon.message')}
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CategoryPage;
